import type {
  BoxGradientOverlay,
  BoxEffects,
  BoxStroke,
  LayerStyleMasks,
  BoxMaskRect,
} from '@lite-workspace/dto-fotoclass-templates';
import type { DrawableEffect } from '../interfaces/drawable-data.interface';

/** Прямоугольник кадра — общая валюта спутниковой математики: `contentRect`, окно обрезки, рамка бокса. */
export interface FrameRect {
  readonly x: number;
  readonly y: number;
  readonly width: number;
  readonly height: number;
}

/** Konva column-major / tiny-skia from_row: `x' = m0·x + m2·y + m4`, `y' = m1·x + m3·y + m5`. */
export type AffineMatrix = readonly [number, number, number, number, number, number];

export function transformEffectOrigins(
  box: { effectReferencePoint?: { x: number; y: number }; effectDocumentOrigin?: { x: number; y: number } },
  m: AffineMatrix,
  scope: 'parent' | 'layer' = 'parent',
): Pick<typeof box, 'effectReferencePoint' | 'effectDocumentOrigin'> {
  const point = (p: { x: number; y: number }): { x: number; y: number } => ({
    x: m[0] * p.x + m[2] * p.y + m[4],
    y: m[1] * p.x + m[3] * p.y + m[5],
  });
  return {
    ...(box.effectReferencePoint && { effectReferencePoint: point(box.effectReferencePoint) }),
    ...(box.effectDocumentOrigin && {
      effectDocumentOrigin: scope === 'parent' ? point(box.effectDocumentOrigin) : box.effectDocumentOrigin,
    }),
  };
}

export function scaleMaskRect(rect: BoxMaskRect, sx: number, sy: number, dx = 0, dy = 0): BoxMaskRect {
  if (rect.transform || (sx !== sy && (rect.rotation ?? 0) % 180 !== 0) || sx < 0 || sy < 0) {
    return transformMaskRect(rect, [sx, 0, 0, sy, dx, dy]);
  }

  return { ...rect, x: rect.x * sx + dx, y: rect.y * sy + dy, width: rect.width * sx, height: rect.height * sy };
}

/** The affine frame is authoritative; never apply its legacy rotation a second time. */
export function transformMaskRect(rect: BoxMaskRect, delta: AffineMatrix): BoxMaskRect {
  const angle = ((rect.rotation ?? 0) * Math.PI) / 180;
  const c = Math.cos(angle),
    s = Math.sin(angle);
  const a = c * rect.width,
    b = s * rect.width,
    d = c * rect.height,
    cc = -s * rect.height;
  const uv: AffineMatrix = rect.transform ?? [
    a,
    b,
    cc,
    d,
    rect.x + rect.width / 2 - (a + cc) / 2,
    rect.y + rect.height / 2 - (b + d) / 2,
  ];
  const m = multiplyAffine(delta, uv);
  const xs = [m[4], m[4] + m[0], m[4] + m[2], m[4] + m[0] + m[2]];
  const ys = [m[5], m[5] + m[1], m[5] + m[3], m[5] + m[1] + m[3]];
  const x = Math.min(...xs),
    y = Math.min(...ys);
  return { ...rect, x, y, width: Math.max(...xs) - x, height: Math.max(...ys) - y, rotation: 0, transform: [...m] };
}

export function multiplyAffine(m: AffineMatrix, n: AffineMatrix): AffineMatrix {
  return [
    m[0] * n[0] + m[2] * n[1],
    m[1] * n[0] + m[3] * n[1],
    m[0] * n[2] + m[2] * n[3],
    m[1] * n[2] + m[3] * n[3],
    m[0] * n[4] + m[2] * n[5] + m[4],
    m[1] * n[4] + m[3] * n[5] + m[5],
  ];
}

/** Parent placement moves both frames; an individual layer edit leaves the PSD document frame fixed. */
export function transformGradientOverlay<T extends Pick<BoxGradientOverlay, 'referenceTransform' | 'documentTransform'>>(
  overlay: T,
  transform: AffineMatrix,
  scope: 'parent' | 'layer' = 'parent',
): T {
  if (!overlay.referenceTransform || !overlay.documentTransform) return overlay;
  return {
    ...overlay,
    referenceTransform: [...multiplyAffine(transform, overlay.referenceTransform)],
    documentTransform: scope === 'parent' ? [...multiplyAffine(transform, overlay.documentTransform)] : overlay.documentTransform,
  };
}

export function transformStrokeGradient<T extends { gradient?: BoxStroke['gradient'] }>(
  stroke: T,
  transform: AffineMatrix,
  scope: 'parent' | 'layer' = 'parent',
): T {
  return stroke.gradient ? { ...stroke, gradient: transformGradientOverlay(stroke.gradient, transform, scope) } : stroke;
}

export function transformDrawableGradientEffect(
  effect: DrawableEffect,
  transform: AffineMatrix,
  scope: 'parent' | 'layer' = 'parent',
): DrawableEffect {
  if (effect.kind === 'gradientOverlay') return transformGradientOverlay(effect, transform, scope);
  return effect.kind === 'stroke' ? transformStrokeGradient(effect, transform, scope) : effect;
}

export function transformBoxGradientEffects<T extends BoxEffects | undefined>(
  effects: T,
  transform: AffineMatrix,
  scope: 'parent' | 'layer' = 'parent',
): T {
  if (!effects) return effects;
  return {
    ...effects,
    ...(effects.gradientOverlay && { gradientOverlay: transformGradientOverlay(effects.gradientOverlay, transform, scope) }),
    ...(effects.extraGradientOverlays && {
      extraGradientOverlays: effects.extraGradientOverlays.map(overlay => transformGradientOverlay(overlay, transform, scope)),
    }),
    ...(effects.strokes && { strokes: effects.strokes.map(stroke => transformStrokeGradient(stroke, transform, scope)) }),
  };
}

/** Source binding is an editor rule; PSD positionRelativeToLayer is retained without inferring linkage. */
export function transformLayerStyleMasks(
  masks: LayerStyleMasks | undefined,
  transform: AffineMatrix,
  scope: 'parent' | 'layer' = 'parent',
): LayerStyleMasks | undefined {
  if (!masks) return masks;
  const transformed: LayerStyleMasks = {};
  for (const stage of ['source', 'post'] as const) {
    if (masks[stage])
      transformed[stage] = masks[stage].map(mask =>
        scope === 'layer' && (stage === 'post' || mask.linked === false)
          ? mask
          : { ...mask, transform: [...multiplyAffine(transform, mask.transform)] },
      );
  }

  return transformed;
}

function axisScale(oldSize: number, newSize: number): number {
  return oldSize === 0 ? 1 : newSize / oldSize;
}

/**
 * Спутник запечён в ТОТ ЖЕ кадр, что и рамка, поэтому запись геометрии, двигающая или меняющая
 * рамку, обязана везти его той же дельтой — иначе окно едет по неподвижному содержимому.
 * Кадр-агностична: работает на абсолютных, коллаж-локальных и групп-локальных координатах одинаково.
 */
export function scaleRectToFrame(oldFrame: FrameRect, newFrame: FrameRect, inner: FrameRect): FrameRect {
  const sx = axisScale(oldFrame.width, newFrame.width);
  const sy = axisScale(oldFrame.height, newFrame.height);

  return {
    x: newFrame.x + (inner.x - oldFrame.x) * sx,
    y: newFrame.y + (inner.y - oldFrame.y) * sy,
    width: inner.width * sx,
    height: inner.height * sy,
  };
}

/** Осевой масштаб вокруг старого начала, перебазированный на новое: то, что делает с кадром запись геометрии. */
export function frameDelta(oldFrame: FrameRect, newFrame: FrameRect): AffineMatrix {
  const sx = axisScale(oldFrame.width, newFrame.width);
  const sy = axisScale(oldFrame.height, newFrame.height);

  return [sx, 0, 0, sy, newFrame.x - oldFrame.x * sx, newFrame.y - oldFrame.y * sy];
}

/**
 * Живой источник смарт-объекта рисуется через свою PSD-матрицу и рамкой только ОБРЕЗАЕТСЯ, поэтому
 * дельта рамки обязана въехать в матрицу — иначе окно едет по содержимому, которое не двигалось.
 */
export function composeSourceTransform(transform: AffineMatrix, oldFrame: FrameRect, newFrame: FrameRect): AffineMatrix {
  return multiplyAffine(frameDelta(oldFrame, newFrame), transform);
}

export function rotatedFrameDelta(oldFrame: FrameRect, newFrame: FrameRect, oldAngle = 0, newAngle = 0): AffineMatrix {
  const rotation = (frame: FrameRect, degrees: number): AffineMatrix => {
    const a = (degrees * Math.PI) / 180;
    const c = Math.cos(a),
      s = Math.sin(a);
    const x = frame.x + frame.width / 2,
      y = frame.y + frame.height / 2;
    return [c, s, -s, c, x - c * x + s * y, y - s * x - c * y];
  };

  return multiplyAffine(
    rotation(newFrame, newAngle),
    multiplyAffine(frameDelta(oldFrame, newFrame), rotation(oldFrame, -oldAngle)),
  );
}

/**
 * The engine's two paths read a window without `transform` differently: plain drawing lays it in the unturned box and
 * ignores its `rotation`, the live layer-style sampler reads that `rotation`. The quad is the one form both read alike.
 */
export function turnableMaskRect(
  rect: BoxMaskRect,
  frame: FrameRect,
  fromAngle: number | undefined,
  toAngle: number | undefined,
): BoxMaskRect {
  if (rect.transform || ((fromAngle ?? 0) === 0 && (toAngle ?? 0) === 0)) return rect;
  return transformMaskRect({ ...rect, rotation: 0 }, rotatedFrameDelta(frame, frame, 0, fromAngle ?? 0));
}

/** Preserve the full quad whenever parent scaling crosses the mask's rotated basis. */
export function maskRectForFrame(rect: BoxMaskRect, from: FrameRect, to: FrameRect, oldAngle = 0, newAngle = 0): BoxMaskRect {
  const m = rotatedFrameDelta(from, to, oldAngle, newAngle);
  const sx = axisScale(from.width, to.width),
    sy = axisScale(from.height, to.height);
  if (rect.transform || (sx !== sy && ((rect.rotation ?? 0) - oldAngle) % 180 !== 0) || sx < 0 || sy < 0) {
    return transformMaskRect(rect, m);
  }

  const cx = rect.x + rect.width / 2,
    cy = rect.y + rect.height / 2;
  const width = rect.width * axisScale(from.width, to.width);
  const height = rect.height * axisScale(from.height, to.height);
  return {
    ...rect,
    x: m[0] * cx + m[2] * cy + m[4] - width / 2,
    y: m[1] * cx + m[3] * cy + m[5] - height / 2,
    width,
    height,
    rotation: (rect.rotation ?? 0) + newAngle - oldAngle,
  };
}

export function gradientEffectsForFrame(
  effects: BoxEffects | undefined,
  from: readonly [number, number, number, number],
  to: readonly [number, number, number, number],
  oldAngle = 0,
  newAngle = 0,
  scope: 'parent' | 'layer' = 'layer',
): BoxEffects | undefined {
  if (!effects) return effects;
  const frame = (r: readonly number[]): FrameRect => ({ x: r[0], y: r[1], width: r[2], height: r[3] });
  return transformBoxGradientEffects(effects, rotatedFrameDelta(frame(from), frame(to), oldAngle, newAngle), scope);
}
